import * as React from 'react';

import { connect } from 'react-redux';
import AppState from '@webapp/store/state';

import styled, { css } from 'styled-components';

import Flag from '@components/basics/flag';
import DropdownItemFlag from './dropdown-item-flag';
import InputField from '@webapp/components/form/input';
import DecorationDropdown from '../decoration-dropdown';
import DropdownCtrl from '@webapp/controllers/form/dropdown';

import { Option } from '@webapp/types/common/option';
import { Country } from '@common/types/data/country';
import { isEmail, isPhone } from '@services/utils/validators';
import { ContactInfo } from '@webapp/types/common/contact-info';
import { normalizePhoneOrEmail } from '@webapp/utils/normalizers';

import Validation from '../validation';
interface EmailOrPhoneCtrlProps {
  className?: string;
  label?: React.ReactNode;
  value?: string;
  error?: React.ReactNode;
  country?: Country;
  decoration?: React.ReactElement | null;
  type?: 'email' | 'telephone';
  propagateClick?: boolean;
  hasClose?: boolean;
  countries: Country[];
  countriesOptions: Option[];
  needsValidation?: boolean;
  hideLabel?: boolean;
  isGrey?: boolean;
  customBorderRadius?: string;
  onClose?: (ev: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
  onChange: (value: ContactInfo) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  onCountryChange?: (value: Country) => void;
  onInputChange?: (value?: string) => void;
  locked?: boolean;
  isPrimary?: boolean;
  enablePhone?: boolean;
}

const FLAG_PAGE_SIZE = 20;

export const DEFAULT_COUNTRY: Country = {
  id: 'us',
  name: 'United States',
  code: '1',
};

const mapStateToProps = (state: AppState) => ({
  countries: state.categories.countries,
  countriesOptions: state.categories.countries.map((c) => ({
    value: c.id,
    label: `${c.name} (+${c.code})`,
  })),
});

const checkCountry = (newValue: string, c: Country) => {
  const result = newValue.startsWith(`+${c.code}`);
  if (result && c.areaCodes) {
    const areaCode = c.areaCodes.find((a) =>
      newValue.startsWith(`+${c.code}${a}`)
    );
    if (!areaCode) {
      return false;
    }
  }
  return result;
};

const updateCountry = (
  newValue: string,
  country: Country,
  countries: Country[],
  onChange?: (value: Country) => void
) => {
  const newCountry = countries.find((c) => checkCountry(newValue, c));
  if (newCountry && newCountry.id !== country.id) {
    if (onChange) {
      onChange(newCountry);
    }
  }
};

export const EmailOrPhoneCtrl: React.FC<EmailOrPhoneCtrlProps> = (props) => {
  const propsValue = props.value || '';
  const { countries, onCountryChange, enablePhone = false } = props;

  const [value, setValue] = React.useState('');
  const [country, setCountry] = React.useState(
    props.country || DEFAULT_COUNTRY
  );
  const [filter, setFilter] = React.useState('');
  const [loaded, setLoaded] = React.useState(false);
  const [lastLength, setLastLength] = React.useState(propsValue.length);
  const [localError, setLocalError] = React.useState('');
  const [isOpen, setOpen] = React.useState(false);
  const [flagPage, setFlagPage] = React.useState(1);

  const countrySetter = React.useCallback(
    (value: Country) => {
      setCountry(value);
      if (onCountryChange) {
        onCountryChange(value);
      }
    },
    [onCountryChange]
  );

  React.useEffect(() => {
    if (!loaded && !value && propsValue && countries.length) {
      if (!checkCountry(propsValue, country)) {
        updateCountry(propsValue, country, countries, countrySetter);
      }

      setValue(propsValue);
      setLastLength(propsValue.length);
      setLoaded(true);
    }
  }, [loaded, propsValue, value, country, countries, countrySetter]);

  React.useEffect(() => {
    if (loaded) {
      setValue(propsValue);
    }
  }, [propsValue, loaded]);

  const isPartialNumber = (newValue: string): boolean => {
    return (
      (!props.type || props.type === 'telephone') &&
      /^\(?\+?[\d\s-()]\)?[\d\s-()]+$/.test(newValue)
    );
  };

  const isPartialEmail = (newValue: string): boolean => {
    return (
      (!props.type || props.type === 'email') && /^[a-zA-Z0-9]+/.test(newValue)
    );
  };

  const handleContactChange = (newValue: string) => {
    props.onInputChange && props.onInputChange(newValue);
    const contact = normalizePhoneOrEmail(newValue);
    if (contact) {
      if (
        !props.type ||
        (props.type === 'email' && contact.type === 'email') ||
        (props.type === 'telephone' && contact.type === 'sms')
      ) {
        props.onChange(contact);
      }
    } else {
      props.onChange({ type: 'email', value: '' });
      if (isPartialNumber(newValue) && enablePhone) {
        if (!newValue.startsWith('+') && !newValue.startsWith('(+')) {
          newValue = `(+${country.code}) ${newValue}`;
        } else {
          if (!country.areaCodes || !checkCountry(newValue, country)) {
            updateCountry(newValue, country, props.countries, countrySetter);
          }
          if (
            lastLength > newValue.length && // we are deleting
            newValue.trim() === `(+${country.code})`
          ) {
            newValue = '';
          }
        }
      } else {
        if (isPartialEmail(newValue) || newValue.includes('@')) {
          const helperIndex = newValue.indexOf(')');
          newValue = newValue
            .replace(/\s+/g, '')
            .substring(helperIndex + 1, newValue.length);

          if (newValue.startsWith('+')) {
            newValue = newValue.replace(/^\+[\d-\s]+ /, '');
          }
        }
      }
    }
    setValue(newValue);
    setLastLength(newValue.length);
    setFilter('');
  };

  const getLabel = () => {
    if (props.hideLabel) {
      return;
    }
    if (props.label) {
      return props.label;
    }
    if (enablePhone && (props.type === 'telephone' || isPartialNumber(value))) {
      return 'Phone Number';
    }
    if (props.type === 'email' || isPartialEmail(value)) {
      return 'Email Address';
    }

    return enablePhone ? 'Email or Phone Number' : 'Email Address';
  };

  const showCountrySelector = () => {
    return (
      enablePhone && (props.type === 'telephone' || isPartialNumber(value))
    );
  };

  const handleCountryChange = (id: string) => {
    if (enablePhone) {
      const newCountry = props.countries.find((c) => c.id === id);

      if (newCountry) {
        countrySetter(newCountry);
        handleContactChange(
          value.replace(/^\(?\+(?:\d-)?\d{1,3}\)?\s?/, `(+${newCountry.code}) `)
        );
      }
    }
  };

  const handleBlur = () => {
    if (props.onBlur) {
      props.onBlur();
    }

    if (isPartialNumber(value) && !isPhone(value)) {
      setLocalError('Invalid Phone Number');
    }

    if (isPartialEmail(value) && !isEmail(value)) {
      setLocalError('Invalid Email Address');
    }
  };

  const handleFocus = () => {
    if (props.onFocus) {
      props.onFocus();
    }
    setLocalError('');
  };

  const leftDecoration = (locked?: boolean) => {
    return (
      <DecorationDropdown
        listOpen={isOpen}
        hideArrow={locked || props.needsValidation}
      >
        <Flag bigFlag squareFlag countryCode={country.id} />
      </DecorationDropdown>
    );
  };

  return (
    <>
      <DropdownCtrl
        className={props.className}
        title="Country"
        value={country.id}
        onChange={(newCountry) => handleCountryChange(newCountry)}
        hasFilter
        disabled={props.locked}
        filterLabel="Find your country"
        customFilterValue={filter}
        onCustomFilter={setFilter}
        onOpen={setOpen}
        getNextPage={(step) => setFlagPage(flagPage + step)}
        header={
          <StyledInputField
            id="email"
            className={`input-email-phone ${
              props.needsValidation ? 'validation' : ''
            }`}
            name="input-field-ctrl"
            label={getLabel()}
            leftDecoration={
              showCountrySelector() ? leftDecoration(props.locked) : undefined
            }
            rightDecoration={props.decoration}
            bigDecoration={showCountrySelector()}
            customBorderRadius={props.customBorderRadius}
            isGrey={props.isGrey}
            error={localError || props.error}
            autoComplete="off"
            value={value}
            locked={props.locked}
            hideLabel={props.needsValidation}
            onChange={(newValue) => handleContactChange(newValue)}
            onBlur={() => handleBlur()}
            onFocus={() => handleFocus()}
            propagateClick={props.propagateClick}
            hasClose={props.hasClose}
            onClose={props.onClose}
          />
        }
      >
        {props.countries
          .filter((c) =>
            filter.length ? c.name.toLowerCase().includes(filter) : true
          )
          .filter((_, i) => i < flagPage * FLAG_PAGE_SIZE)
          .map((c) => (
            <DropdownItemFlag
              key={c.id}
              countryId={c.id}
              countryCode={c.code}
              countryName={c.name}
              selected={country === c}
              onClick={() => handleCountryChange(c.id)}
            />
          ))}
      </DropdownCtrl>
      {props.needsValidation ? (
        <Validation
          className="mb-10"
          address={value}
          type={props.type === 'email' ? 'email' : 'phone'}
          isPrimary={!!props.isPrimary}
        />
      ) : null}
    </>
  );
};

export default connect(mapStateToProps)(EmailOrPhoneCtrl);

const StyledInputField = styled(InputField)<{ customBorderRadius?: string }>`
  ${({ customBorderRadius }) =>
    customBorderRadius
      ? css`
          border-radius: ${customBorderRadius};
        `
      : null}

  &.validation {
    background-color: ${({ theme }) => theme.colors.primary};
    border-radius: 4px 4px 0 0;
    border-left-color: ${({ theme }) => theme.colors.primary};
    input {
      color: ${({ theme }) => theme.colors.differentBackground};
    }
  }
`;
